import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, MapPin, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { DELIVERY_MODE, ORDER_STATUS, formatSlotWindow } from '../../api/orders';
import { startDelivery, DELIVERY_ERRORS } from '../../api/delivery';
import Button from '../../components/Button';
import StatusBadge from '../../components/StatusBadge';
import DriverShell from './DriverShell';
import DriverBillSummary from './DriverBillSummary';
import { useDriverOrder } from './useDriverOrder';
import cardStyles from './DriverCards.module.css';
import styles from './DriverRideDetail.module.css';

function friendlyError(err) {
  return DELIVERY_ERRORS[err.message] || err.message || 'Something went wrong.';
}

export default function DriverRideDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notify } = useOrders();
  const { order, loading, error } = useDriverOrder(orderId);
  const [starting, setStarting] = useState(false);

  const handleStart = async () => {
    setStarting(true);
    try {
      await startDelivery(order.id, user.idToken);
      notify(`Order ${order.id} is now out for delivery`);
      navigate(`/driver/rides/${order.id}/proof`);
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setStarting(false);
    }
  };

  return (
    <DriverShell>
      <header className={cardStyles.header}>
        <button className={cardStyles.backBtn} onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 className={`${cardStyles.headerTitle} ${styles.headerTitleMono}`}>
          {order ? order.id : loading ? 'Loading…' : 'Ride not found'}
        </h1>
      </header>

      {!loading && error && <p className={cardStyles.content}>{error}</p>}

      {order && (
        <>
          <main className={cardStyles.content}>
            <div className={styles.statusRow}>
              <StatusBadge status={order.status} />
              {order.deliveryMode === DELIVERY_MODE.SCHEDULED && (
                <span className={styles.scheduleTag}>
                  <Clock size={13} /> {formatSlotWindow(order.slotStart, order.slotEnd) || 'Scheduled'}
                </span>
              )}
            </div>

            <section className={cardStyles.card}>
              <div className={cardStyles.cardHeadRow}>
                <p className={cardStyles.cardLabel}>Customer Information</p>
                {order.customer.phone && (
                  <a
                    className={styles.callBtn}
                    href={`tel:${order.customer.phone}`}
                    aria-label={`Call ${order.customer.name || 'customer'}`}
                  >
                    <Phone size={15} />
                  </a>
                )}
              </div>
              <p className={styles.customerName}>{order.customer.name || 'Customer'}</p>
              {order.customer.phone ? (
                <p className={styles.customerPhone}>{order.customer.phone}</p>
              ) : (
                <p className={styles.customerPhoneMuted}>Phone not available</p>
              )}
            </section>

            {order.address && (
              <section className={cardStyles.card}>
                <p className={cardStyles.cardLabel}>Delivery Address</p>
                <p className={styles.addressText}>
                  <MapPin size={14} /> {order.address}
                </p>
              </section>
            )}

            <DriverBillSummary order={order} />

            {order.notes && (
              <section className={cardStyles.card}>
                <p className={cardStyles.cardLabel}>Delivery Notes</p>
                <p className={cardStyles.notesText}>{order.notes}</p>
              </section>
            )}
          </main>

          {order.status === ORDER_STATUS.PACKED && (
            <div className={cardStyles.ctaBar}>
              <Button
                size="lg"
                variant="primary"
                className={cardStyles.ctaBtn}
                disabled={starting}
                onClick={handleStart}
              >
                {starting ? 'Starting…' : 'Start Delivery'}
              </Button>
            </div>
          )}

          {order.status === ORDER_STATUS.OUT_FOR_DELIVERY && (
            <div className={cardStyles.ctaBar}>
              <Button
                size="lg"
                variant="primary"
                className={cardStyles.ctaBtn}
                onClick={() => navigate(`/driver/rides/${order.id}/proof`, { state: { order } })}
              >
                Give Delivery
              </Button>
            </div>
          )}
        </>
      )}
    </DriverShell>
  );
}
